const ACTIVE_DOT_CLASS = 'best-books-recommendation-carousel__dot--active'
const ENHANCED_CAROUSEL_CLASS = 'best-books-recommendation-carousel--enhanced'
const DRAG_THRESHOLD = 4
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'
const initializedCarousels = new WeakSet<HTMLElement>()

/** Measure a slide's horizontal position relative to the first slide. */
function slideScrollLeft(slides: HTMLElement[], index: number): number {
  return slides[index].offsetLeft - slides[0].offsetLeft
}

/** Find the slide whose snap position is nearest the track's current scroll. */
function nearestSlideIndex(track: HTMLElement, slides: HTMLElement[]): number {
  let nearestIndex = 0
  let nearestDistance = Number.POSITIVE_INFINITY
  for (let index = 0; index < slides.length; index += 1) {
    const distance = Math.abs(track.scrollLeft - slideScrollLeft(slides, index))
    if (distance < nearestDistance) {
      nearestDistance = distance
      nearestIndex = index
    }
  }
  return nearestIndex
}

/** Keep visual and accessible pagination state on the same active dot. */
function updateDots(dots: HTMLButtonElement[], activeIndex: number): void {
  for (let index = 0; index < dots.length; index += 1) {
    const isActive = index === activeIndex
    dots[index].classList.toggle(ACTIVE_DOT_CLASS, isActive)
    if (isActive) {
      dots[index].setAttribute('aria-current', 'true')
    } else {
      dots[index].removeAttribute('aria-current')
    }
  }
}

/**
 * Enhance rendered Best Books carousels without replacing native scrolling.
 *
 * Dot clicks and arrow keys move one slide without looping. Scroll events keep
 * the active dot synchronized, while mouse and pen pointers gain drag support;
 * touch and trackpad input remain browser-native. Each carousel is initialized
 * once, and reduced-motion users always receive an instant scroll.
 */
export function initializeBestBooksCarousels(
  root: ParentNode = document,
): void {
  const carousels = root.querySelectorAll<HTMLElement>(
    '[data-best-books-recommendation-carousel]',
  )

  for (const carousel of carousels) {
    if (initializedCarousels.has(carousel)) {
      continue
    }

    const track = carousel.querySelector<HTMLElement>(
      '[data-best-books-carousel-track]',
    )
    const slides = Array.from(
      carousel.querySelectorAll<HTMLElement>(
        '[data-best-books-carousel-slide]',
      ),
    )
    const dots = Array.from(
      carousel.querySelectorAll<HTMLButtonElement>(
        '[data-best-books-carousel-dot]',
      ),
    )
    if (!track || slides.length < 2 || dots.length !== slides.length) {
      continue
    }
    initializedCarousels.add(carousel)

    let activeIndex = 0
    let drag:
      | {
          moved: boolean
          pointerId: number
          startScrollLeft: number
          startX: number
        }
      | undefined
    let suppressClick = false

    const setActiveIndex = (index: number): void => {
      activeIndex = Math.max(0, Math.min(index, slides.length - 1))
      updateDots(dots, activeIndex)
    }

    const scrollToIndex = (index: number): void => {
      setActiveIndex(index)
      track.scrollTo({
        behavior: window.matchMedia(REDUCED_MOTION_QUERY).matches
          ? 'auto'
          : 'smooth',
        left: slideScrollLeft(slides, activeIndex),
      })
    }

    const navigate = (
      event: KeyboardEvent,
      fromIndex: number,
      moveFocus: boolean,
    ): void => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return
      }
      event.preventDefault()
      const direction = event.key === 'ArrowRight' ? 1 : -1
      const nextIndex = Math.max(
        0,
        Math.min(fromIndex + direction, slides.length - 1),
      )
      scrollToIndex(nextIndex)
      if (moveFocus) {
        dots[nextIndex].focus()
      }
    }

    for (let index = 0; index < dots.length; index += 1) {
      dots[index].addEventListener('click', () => scrollToIndex(index))
      dots[index].addEventListener('keydown', (event) => {
        navigate(event, index, true)
      })
    }

    track.addEventListener('keydown', (event) => {
      if (event.target === track) {
        navigate(event, activeIndex, false)
      }
    })
    track.addEventListener(
      'scroll',
      () => setActiveIndex(nearestSlideIndex(track, slides)),
      { passive: true },
    )

    track.addEventListener('pointerdown', (event) => {
      if (
        (event.pointerType !== 'mouse' && event.pointerType !== 'pen') ||
        event.button !== 0 ||
        (event.target instanceof Element &&
          event.target.closest(
            'a, button, input, select, textarea, [contenteditable]',
          ))
      ) {
        return
      }
      drag = {
        moved: false,
        pointerId: event.pointerId,
        startScrollLeft: track.scrollLeft,
        startX: event.clientX,
      }
      track.classList.add('best-books-recommendation-carousel__track--dragging')
      track.setPointerCapture?.(event.pointerId)
    })

    track.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.pointerId) {
        return
      }
      const distance = drag.startX - event.clientX
      if (!drag.moved && Math.abs(distance) < DRAG_THRESHOLD) {
        return
      }
      drag.moved = true
      track.scrollLeft = drag.startScrollLeft + distance
      setActiveIndex(nearestSlideIndex(track, slides))
      event.preventDefault()
    })

    const finishDrag = (event: PointerEvent): void => {
      if (!drag || event.pointerId !== drag.pointerId) {
        return
      }
      const moved = drag.moved
      if (track.hasPointerCapture?.(drag.pointerId)) {
        track.releasePointerCapture(drag.pointerId)
      }
      drag = undefined
      track.classList.remove(
        'best-books-recommendation-carousel__track--dragging',
      )
      if (moved) {
        suppressClick = true
        scrollToIndex(nearestSlideIndex(track, slides))
        window.setTimeout(() => {
          suppressClick = false
        })
      }
    }

    track.addEventListener('pointerup', finishDrag)
    track.addEventListener('pointercancel', finishDrag)
    track.addEventListener(
      'click',
      (event) => {
        if (suppressClick) {
          suppressClick = false
          event.preventDefault()
          event.stopPropagation()
        }
      },
      true,
    )

    setActiveIndex(nearestSlideIndex(track, slides))
    carousel.classList.add(ENHANCED_CAROUSEL_CLASS)
  }
}
